import { useToast } from '@chakra-ui/react'
import { DailyAudio, DailyProvider } from '@daily-co/daily-react'
import { trackEvent } from '@lib/clinician-tracking'
import { DateTime } from 'luxon'
import React, {
  createContext,
  type FC,
  type ReactNode,
  useContext,
  useEffect,
  useState
} from 'react'
import {
  type BlueprintDailyRoomConfiguration,
  useBlueprintDailyCall
} from '../hooks/use-blueprint-daily-call'
import { useClinicianTelehealthSession } from './clinician-telehealth-session.context'

// Define the context type
export type ClinicianDailyManagerContextType = {
  endCall: () => Promise<void>
  isCallEnded: boolean
  configureRoom: (props: BlueprintDailyRoomConfiguration) => void
  isInCall: boolean
  hasLeftCall: boolean
  isBackgroundBlurred: boolean
  toggleBackgroundBlur: () => void
  admitWaitingParticipant: (participantId: string) => Promise<void>
  joinCall: () => Promise<void>
  hasTranscriptionError: boolean
  kickParticipant: (participantId: string) => Promise<void>
}

// Create the context with default values
export const ClinicianDailyManagerContext =
  createContext<ClinicianDailyManagerContextType>({
    endCall: async () => {},
    configureRoom: () => {},
    isInCall: false,
    hasLeftCall: false,
    isBackgroundBlurred: false,
    toggleBackgroundBlur: () => {},
    admitWaitingParticipant: async () => {},
    joinCall: async () => {},
    isCallEnded: false,
    hasTranscriptionError: false,
    kickParticipant: async () => {}
  })

interface ClinicianDailyProviderProps {
  children: ReactNode
}

export const ClinicianDailyProvider: FC<ClinicianDailyProviderProps> = ({
  children
}) => {
  const {
    setAdmitParticipantDialogProps,
    sessionId,
    markSessionStartedRecording,
    recordingStartedAt
  } = useClinicianTelehealthSession()

  const {
    callObject,
    isInCall,
    isCallEnded,
    hasLeftCall,
    roomConfiguration,
    setRoomConfiguration,
    isBackgroundBlurred,
    toggleBackgroundBlur,
    _sharedOnJoinCall,
    leaveCall,
    setIsCallEnded,
    hasTranscriptionError
  } = useBlueprintDailyCall()

  const startInitialTranscription = async () => {
    if (!sessionId) {
      return
    }

    markSessionStartedRecording()
    _markSessionStarted()
    callObject?.startTranscription({ model: 'nova-2' })
    callObject?.startRecording({ layout: { preset: 'audio-only' } })
  }

  const _markSessionStarted = () => {
    callObject?.setMeetingSessionData({
      sessionStarted: true
    })
  }

  const toast = useToast()

  const kickParticipant = async (participantId: string) => {
    await callObject?.updateParticipant(participantId, {
      eject: true
    })

    trackEvent('event:telehealth-session.kick-participant')
  }

  const admitWaitingParticipant = async (participantId: string) => {
    const waitingParticipants = callObject?.waitingParticipants() || {}
    const participantIds = Object.keys(waitingParticipants)

    if (participantIds.length === 0 || !waitingParticipants[participantId]) {
      return
    }

    if (
      recordingStartedAt &&
      DateTime.now().diff(DateTime.fromISO(recordingStartedAt), 'seconds')
        .seconds > 5
    ) {
      const waitingParticipant = waitingParticipants[participantId]
      setAdmitParticipantDialogProps({
        isOpen: true,
        requestingParticipantName: waitingParticipant.name,
        onAdmit: () => {
          callObject?.updateWaitingParticipant(participantId, {
            grantRequestedAccess: true
          })
          setAdmitParticipantDialogProps({
            isOpen: false
          })
        },
        onClose: () => {
          setAdmitParticipantDialogProps({
            isOpen: false
          })
        }
      })
    } else {
      callObject?.updateWaitingParticipant(participantId, {
        grantRequestedAccess: true
      })
    }

    trackEvent('event:telehealth-session.admit-waiting-participant')

    toast.close(participantId)
  }

  const joinCall = async () => {
    await callObject?.join({
      url: roomConfiguration.url,
      token: roomConfiguration.token,
      sendSettings: {
        video: {
          // See: https://www.daily.co/blog/introducing-daily-adaptive-bitrate/
          allowAdaptiveLayers: true
        }
      }
    })

    callObject?.on('participant-joined', () => {
      const meetingSessionState: any = callObject?.meetingSessionState().data

      if (
        !meetingSessionState?.sessionStarted &&
        (callObject?.participantCounts()?.present ?? 0) > 1
      ) {
        startInitialTranscription()
      }
    })

    _sharedOnJoinCall()
  }

  const endCall = async () => {
    setIsCallEnded(true)
    await leaveCall()
  }

  const _resumeCall = async () => {
    await joinCall()
    // Need to remark the session in case the meeting was recreated in the Daily room (everyone left call)
    _markSessionStarted()
    callObject?.startTranscription({ model: 'nova-2' })
    callObject?.startRecording({ layout: { preset: 'audio-only' } })
  }

  useEffect(() => {
    // Wait for call object to initiate
    if (
      callObject?.accessState()?.access === 'unknown' &&
      roomConfiguration.isResuming
    ) {
      _resumeCall()
    }
  }, [callObject, roomConfiguration])

  const configureRoom = (config: BlueprintDailyRoomConfiguration) => {
    setRoomConfiguration(config)
  }

  return (
    <ClinicianDailyManagerContext.Provider
      value={{
        endCall,
        isCallEnded,
        configureRoom,
        isInCall,
        joinCall,
        hasLeftCall,
        isBackgroundBlurred,
        toggleBackgroundBlur,
        admitWaitingParticipant,
        hasTranscriptionError,
        kickParticipant
      }}
    >
      <DailyProvider callObject={callObject}>
        {children}
        <DailyAudio />
      </DailyProvider>
    </ClinicianDailyManagerContext.Provider>
  )
}

export const useClinicianDailyManager = () => {
  return useContext(ClinicianDailyManagerContext)
}
